import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Drawer } from "antd";
import Button from "@dtci/spellbook/Button";
import { UserForm } from "./user-form/index.jsx";
import "./styles.scss";
import { tapApi } from "tap/api.js";
import { useLoading } from "tap/utils/hooks/useLoading.js";
import { useToasts } from "tap/utils/hooks/useToasts.js";
import { useUsersActions } from "tap/store/users/useUsers.js";
import { useConfirmationDialog } from "tap/components/dialogs/dialogs/confirmation.jsx";
import { useUsersData } from "../../hooks/useUsersData";
import { usePermissions } from "tap/utils/permissions.js";
import { getFormValue } from "../../utils/userHelpers";

/**
 * Validates user form data.
 * @param {Object} formData - The form data to validate
 * @param {boolean} isCreate - Whether in create mode
 * @returns {Object} - Object containing validation errors
 */
const validateUserForm = (formData, isCreate) => {
  const errors = {};

  if (isCreate) {
    if (!formData?.username?.trim()) {
      errors.username = "Username is required";
    }
  }

  return errors;
};

/**
 * User Drawer Component
 *
 * Features:
 * - Renders a drawer UI for creating or editing a user
 * - Handles user creation, update, and delete actions via API
 * - Manages loading and toast notifications for user actions
 * - Resets form and scrolls to top on open
 *
 * @returns {Element}
 * @constructor
 */
export const UserDrawer = () => {
  const { drawerMode, selectedUser, userForm } = useSelector(
    (state) => state.users,
  );

  const { updateUsers } = useUsersData();
  const { clearUserForm } = useUsersActions();
  const { setLoadingTrue, setLoadingFalse } = useLoading();
  const { showSuccessToast, showErrorToast } = useToasts();
  const { openConfirmationDialog } = useConfirmationDialog();
  const { isAdmin } = usePermissions();

  // Separate visibility state allows close animation to complete before clearing Redux state
  const [isOpen, setIsOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const isCreate = drawerMode === "create";

  useEffect(() => {
    if (drawerMode !== null) {
      setIsOpen(true);
    }
  }, [drawerMode]);

  const handleAfterOpenChange = (open) => {
    if (!open && drawerMode !== null) {
      clearUserForm();
      setValidationErrors({});
      updateUsers({ drawerMode: null });
    }
  };

  useEffect(() => {
    let timeoutId;
    if (isOpen) {
      timeoutId = setTimeout(() => {
        const el = document.querySelector(".ant-drawer-body");
        if (el) el.scrollTop = 0;
      }, 0);
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isOpen]);

  const handleClose = () => {
    setIsOpen(false);
  };

  const triggerRefresh = () => {
    updateUsers({ refreshUsers: true });
  };

  const handleCreateUser = async () => {
    // Validate form before submitting
    const errors = validateUserForm(userForm, true);
    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    setLoadingTrue();
    try {
      const user = await tapApi.createUser({
        username: userForm?.username,
        spaUserRoleIds: userForm.spaUserRoleIds.map((role) => role.value),
      });

      if (user?.code || user?.httpCode || user?.error) {
        showErrorToast(
          user?.message || user?.error?.message || "Failed to create user",
        );
        return;
      }

      updateUsers({
        drawerMode: "edit",
        selectedUser: user,
        refreshUsers: true,
      });

      showSuccessToast("User created successfully.");
    } catch (error) {
      showErrorToast(error?.message || "Failed to create user");
    } finally {
      setLoadingFalse();
    }
  };

  const handleSaveUser = async () => {
    setLoadingTrue();
    try {
      const res = await tapApi.updateUser({
        id: selectedUser?.id,
        createdAt: selectedUser?.createdAt,
        modifiedAt: selectedUser?.modifiedAt,
        lastLogin: selectedUser?.lastLogin,
        username: getFormValue(
          userForm?.username,
          selectedUser?.username || "",
        ),
        fullName: getFormValue(
          userForm?.fullName,
          selectedUser?.fullName || "",
        ),
        ermUserName: selectedUser?.ermUserName || "",
        status:
          userForm?.status !== ""
            ? userForm?.status.value
            : selectedUser?.status,
        rightslineId: getFormValue(
          userForm?.rightslineId,
          selectedUser?.rightslineId || "",
        ),
        spaUserRoleIds: userForm?.spaUserRoleIds
          ? userForm?.spaUserRoleIds.map((role) => role.value)
          : (selectedUser?.spaUserRoleIds ?? []),
        tokenHash: selectedUser?.tokenHash || "",
      });

      if (res?.code || res?.httpCode || res?.error) {
        showErrorToast(
          res?.message || res?.error?.message || "Failed to update user.",
        );
        return;
      }

      updateUsers({ refreshUsers: true });
      setIsOpen(false);
      showSuccessToast("User updated successfully.");
    } catch (error) {
      showErrorToast(error?.message || "Unexpected error updating user.");
      console.error("Unexpected error updating user:", error);
    } finally {
      setLoadingFalse();
    }
  };

  const onOpenDeleteDialog = () => {
    handleClose();
    openConfirmationDialog(
      "Delete User",
      "Confirm",
      [`Are you sure you wish to delete this user?`],
      async () => {
        setLoadingTrue();
        try {
          const res = await tapApi.deleteUser(selectedUser.id);

          if (res?.code || res?.httpCode || res?.error) {
            showErrorToast(
              res?.message || res?.error?.message || "Failed to delete user.",
            );
            return;
          }

          updateUsers({ refreshUsers: true });
          showSuccessToast("User deleted successfully.");
        } catch (error) {
          showErrorToast("Error deleting user.");
          console.error("Error deleting user:", error);
        } finally {
          setLoadingFalse();
        }
      },
    );
  };

  const renderFooter = () => {
    if (isCreate) {
      return (
        <div className="button-row">
          <Button
            iconType="fa-solid fa-xmark"
            labelText="close"
            styleType="link"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            iconType="fa-solid fa-floppy-disk"
            labelText="create"
            styleType="link"
            onClick={handleCreateUser}
          >
            Create User
          </Button>
        </div>
      );
    }

    if (!isAdmin) return null;

    return (
      <div className="button-row">
        <Button
          iconType="fa-solid fa-trash"
          labelText="delete"
          styleType="link"
          onClick={onOpenDeleteDialog}
        >
          Delete
        </Button>
        <Button
          iconType="fa-solid fa-floppy-disk"
          labelText="save"
          styleType="link"
          onClick={handleSaveUser}
        >
          Update User
        </Button>
      </div>
    );
  };

  const drawerClassName = `user-drawer ${isCreate ? "user-drawer--create" : "user-drawer--edit"}`;

  return (
    <Drawer
      className={drawerClassName}
      title={isCreate ? "Create User" : "Edit User"}
      width={750}
      placement="right"
      open={isOpen}
      onClose={handleClose}
      afterOpenChange={handleAfterOpenChange}
      footer={renderFooter()}
    >
      <UserForm isCreate={isCreate} validationErrors={validationErrors} />
    </Drawer>
  );
};
